import React from "react";
import PropTypes from "prop-types";
import radium, { Style } from "radium";
import Helmet from "react-helmet";
import shallowCompare from "react-addons-shallow-compare";
import Waypoint from "react-waypoint";
import Sticky from "react-stickynode";
import noScroll from "no-scroll";
import truncate from "truncate";
import Container from "backpack-ui/dist/components/container";
import Overlay from "backpack-ui/dist/components/overlay";
import IconButton from "backpack-ui/dist/components/iconButton";
import PageHeader from "backpack-ui/dist/components/pageHeader";
import { Heading } from "backpack-ui/dist/components/text";
import colors from "backpack-ui/dist/styles/colors";
import mq from "backpack-ui/dist/styles/mq";
import {
  fontSizeBodySmall,
  fontSizeHeading6,
  fontSizeHeading7,
  fontWeightLight,
  fontWeightRegular,
  fontWeightMedium,
  lineHeightHeading7,
} from "backpack-ui/dist/styles/typography";
import zIndex from "backpack-ui/dist/styles/zIndex";
import timing from "backpack-ui/dist/styles/timing";
import { connect } from "react-redux";
import Breadcrumbs from "./breadcrumbs";
import NarrativeContentSection from "./narrativeContentSection";
import NarrativeSideMenu from "./narrativeSideMenu";
import FlightQuote from "./flightQuote";
import WorldNomadsWidget from "./worldNomadsWidget";
import DataLayer from "../../shared/components/dataLayer";
import { generateTitleId } from "../utils/generateTitleId";

import {
  createDetailUrlFromResource,
} from "../../../../server/utils/createUrls";

const styles = {
  breadcrumbs: {
    display: "block",
    padding: "16px",
  },

  menu: {
    maxHeight: "100vh",
    [`@media (max-width: ${mq.max["768"]})`]: {
      backgroundColor: colors.bgPrimary,
      marginLeft: "-30px",
      opacity: 0,
      overflow: "hidden",
      paddingLeft: "30px",
      paddingRight: "30px",
      pointerEvents: "none",
      position: "absolute",
      transition: `opacity ${timing.default} 100ms`,
      width: "100vw",
      zIndex: zIndex.below,
    },

    [`@media (max-width: ${mq.max["480"]})`]: {
      marginLeft: "-15px",
      paddingLeft: "15px",
      paddingRight: "15px",
    },
  },

  overlay: {
    top: "420px",
    [`@media (min-width: ${mq.min["768"]})`]: {
      display: "none",
    },
  },
  overlayTop: {
    top: 0,
  },
  header: {
    marginBottom: "56px",
    minHeight: "77px",
    paddingBottom: "24px",
    paddingTop: "24px",
  },

  menuOpen: {
    [`@media (max-width: ${mq.max["768"]})`]: {
      opacity: 1,
      zIndex: zIndex.menu,
      pointerEvents: "auto",
      overflowY: "auto",
    },
  },

  menuFixed: {
    [`@media (max-width: ${mq.max["768"]})`]: {
      position: "fixed",
      top: 0,
    },

    [`@media (min-width: ${mq.min["768"]})`]: {
      right: "30px",
    },

    [`@media (min-width: ${mq.min["768"]})`]: {
      right: "60px",
    },
  },

  menuTriggerButton: {
    float: "right",
    marginTop: "22px",
    position: "relative",
    top: 0,
    right: 0,
    zIndex: zIndex.modal,

    [`@media (min-width: ${mq.min["768"]})`]: {
      display: "none",
    },
  },

  triggerButtonFixed: {
    [`@media (max-width: ${mq.max["768"]})`]: {
      marginTop: 0,
      position: "fixed",
      right: "30px",
      top: "22px",
    },

    [`@media (max-width: ${mq.max["480"]})`]: {
      right: "15px",
    },
  },

  featured: {
    textAlign: "left",
    fontSize: `${fontSizeHeading6}px`,
    lineHeight: 32 / fontSizeHeading6,
    marginBottom: "32px",
    color: "#586575",
    fontWeight: fontWeightLight,

    [`@media (min-width: ${mq.min["768"]})`]: {
      fontSize: "28px",
      marginBottom: "72px",
      lineHeight: `${40 / 28}`,
    },
  },
};

const tableContainerRules = {
  ".PageContent .active h2": {
    borderBottom: `1px solid ${colors.borderPrimary}`,
    marginLeft: "-15px",
    paddingLeft: "15px",
    paddingRight: "15px",
    width: "100vw",
  },
  ".table-container": {
    marginTop: "48px",
  },
  ".row-container": {
    borderTop: `1px solid ${colors.borderPrimary}`,
    display: "flex",
    flexFlow: "wrap",
    paddingBottom: "32px",
    paddingTop: "32px",
  },
  ".name-container": {
    flex: "1 100%",
    marginBottom: "32px",
  },
  ".name-container p": {
    fontSize: "20px",
    fontWeight: fontWeightMedium,
    marginBottom: 0,
  },
  ".element-container": {
    flex: "0 0 100%",
  },
  ".element-container h6": {
    color: "#969FA3",
    fontSize: fontSizeBodySmall,
    lineHeight: 24 / fontSizeBodySmall,
    marginBottom: "4px",
  },
  ".element-container h6 strong": {
    fontWeight: fontWeightRegular,
  },
  ".element-container p": {
    fontSize: `${fontSizeHeading7}px`,
    lineHeight: lineHeightHeading7,
    marginBottom: "24px",
    width: "85%",
  },
  table: {
    width: "100%",
    marginBottom: "16px",
  },
  "table tr": {
    borderBottom: `1px solid ${colors.borderPrimary}`,
  },
  "table tr:last-child": {
    borderBottom: "none",
  },
  "table tr td": {
    paddingTop: "16px",
    paddingBottom: "16px",
    fontSize: "14px",
  },
  "table tr td:not(:first-of-type)": {
    textAlign: "right",
  },
  mediaQueries: {
    [`(min-width: ${mq.min["480"]})`]: {
      ".PageContent .active h2": {
        marginLeft: "-30px",
        paddingLeft: "30px",
        paddingRight: "30px",
      },
    },

    [`(min-width: ${mq.min["768"]})`]: {
      ".element-container": {
        flex: "0 0 50%",
      },
    },
  },
};

class NarrativeComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      activeLink: "",
      activeLinkTitle: "",
      isButtonFixed: true,
      isMenuFixed: true,
      isMenuOpen: false,
      mobileStuck: false,
    };

    this.getPoiUrl = this.getPoiUrl.bind(this);
    this.setActiveLink = this.setActiveLink.bind(this);
    this.toggleMenuFixed = this.toggleMenuFixed.bind(this);
    this.toggleMenuOpen = this.toggleMenuOpen.bind(this);
    this.handleMobileSticky = this.handleMobileSticky.bind(this);
  }

  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }

  getPoiUrl() {
    const { poi, params, breadcrumb } = this.props;

    return createDetailUrlFromResource(
      poi,
      breadcrumb.map(b => b.title),
      params.placeId
    );
  }

  setActiveLink(id, title) {
    this.setState({
      activeLink: id,
      activeLinkTitle: title,
    });
  }

  toggleMenuOpen() {
    this.setState({
      isMenuOpen: !this.state.isMenuOpen,
    }, () => {
      if (this.state.isMenuOpen) {
        noScroll.on();
      } else {
        noScroll.off();
      }
    });
  }

  toggleMenuFixed() {
    this.setState({
      isMenuFixed: !this.state.isMenuFixed,
      isButtonFixed: !this.state.isButtonFixed,
    });
  }

  handleMobileSticky(status) {
    this.setState({
      mobileStuck: status.status === Sticky.STATUS_FIXED,
    });
  }

  render() {
    const { poi, breadcrumb, place, isMobile } = this.props;
    const poiId = `poi-${poi.id}`;
    const narrativeIntro = "intro";
    const categoryLink = `https://www.lonelyplanet.com/places/${poi.containingPlace.id}#in_detail`;
    const topLevelSections = poi.sections.filter(section => section.level === 0);
    let featuredParagraph;
    let featuredContent;

    if (poi.featured) {
      featuredParagraph = poi.featured.slice(
        poi.featured.indexOf("<p>"),
        poi.featured.indexOf("</p>") + 4,
      );

      featuredContent = poi.featured.substring(featuredParagraph.length);
    }

    const truncatedBreadcumbs = isMobile ? breadcrumb.slice(1) : breadcrumb;

    const getSectionsText = (section) => {
      const currText = section.text;
      let subsectionText = "";
      if (section.subsections) {
        subsectionText = section.subsections.map(getSectionsText);
      }
      return [currText, ...subsectionText].join(" ");
    };

    const getFullBodyText = () => {
      const allText = poi.sections.map(getSectionsText);
      const htmlRegex = /<[^>]+>/g;
      const newLineRegex = /\r?\n|\r/g;
      return [featuredParagraph, featuredContent, ...allText]
        .join(" ")
        .replace(htmlRegex, "")
        .replace(newLineRegex, " ");
    };

    return (
      <div
        className="PageContainer NarrativeContainer"
        id={poiId}
        key={poiId}
      >
        <DataLayer
          data={{
            content_group_2: "content_expansion",
          }}
        />

        <Helmet
          meta={[
            { name: "body", class: "swiftype", "data-type": "text", content: getFullBodyText() },
            { name: "op-type-narrative", class: "swiftype", "data-type": "integer", content: "100" },
          ]}
        />

        <Style
          scopeSelector=".NarrativeContainer"
          rules={tableContainerRules}
        />

        <Breadcrumbs
          links={truncatedBreadcumbs}
          style={styles.breadcrumbs}
        />

        <Container className="Page Page--narrative">
          <PageHeader
            heading={poi.name}
            title={`${truncate(place.name, 25)} in detail`}
            titleHref={categoryLink}
            contained
          />

          {poi.menu &&
            <IconButton
              iconName={this.state.isMenuOpen ? "triangleUp" : "triangleDown"}
              backgroundColor={colors.bgPrimary}
              color={colors.linkPrimary}
              onClick={this.toggleMenuOpen}
              shadow
              style={[
                styles.menuTriggerButton,
                this.state.isButtonFixed && styles.triggerButtonFixed,
              ]}
            />
          }

          {isMobile &&
            <Waypoint
              onEnter={this.toggleMenuFixed}
              onLeave={this.toggleMenuFixed}
              fireOnRapidScroll
            />
          }

          {poi.menu &&
            <aside
              className="PageSidebar"
              aria-hidden={!this.state.isMenuOpen}
              style={[
                styles.menu,
                this.state.isMenuFixed && styles.menuFixed,
                this.state.isMenuOpen && styles.menuOpen,
              ]}
            >
              <Sticky
                bottomBoundary=".PageContent"
                enabled={!isMobile}
              >
                <NarrativeSideMenu
                  links={poi.menu.map(item => ({
                    title: item,
                    href: generateTitleId(item),
                  }))}
                  activeLink={this.state.activeLink}
                  narrativeIntro={poi.featured && narrativeIntro}
                  toggleMenu={isMobile ? () => { this.toggleMenuOpen(); } : null}
                  isMobile={isMobile}
                  showInsuranceCallout={poi.showWorldNomads}
                />
              </Sticky>
            </aside>
          }

          {poi.menu &&
            <Overlay
              attached={this.state.isMenuOpen}
              onClick={this.toggleMenuOpen}
              visible={this.state.isMenuOpen}
              style={[
                styles.overlay,
                this.state.mobileStuck && styles.overlayTop,
              ]}
            />
          }

          <div className="PageContent">
            <div className="PageContent--opening">
              {isMobile &&
                <Sticky
                  bottomBoundary={".PageContent"}
                  enabled
                  innerZ={zIndex.default}
                  onStateChange={this.handleMobileSticky}
                >
                  {this.state.isMenuFixed &&
                    <Heading
                      level={2}
                      size={6}
                      weight="medium"
                      style={[
                        styles.header,
                        {
                          backgroundColor: colors.bgPrimary,
                          borderBottom: `1px solid ${colors.borderPrimary}`,
                        },
                      ]}
                    >
                      {this.state.activeLinkTitle}
                    </Heading>
                  }
                </Sticky>
              }

              {poi.featured &&
                <div ref={node => { this.intro = node; }}>
                  <Waypoint
                    onEnter={() => { this.setActiveLink(narrativeIntro, "Intro"); }}
                    bottomOffset="90%"
                    topOffset={
                      this.intro && (
                        (this.intro.offsetHeight +
                          (this.flightQuoteWidget && this.flightQuoteWidget.offsetHeight) ||
                          (this.worldNomadsWidget && this.worldNomadsWidget.offsetHeight) ||
                          0
                        ) * -1
                      )
                    }
                    fireOnRapidScroll
                  />

                  {isMobile &&
                    <Heading
                      id={narrativeIntro}
                      level={2}
                      size={6}
                      weight="medium"
                      style={styles.header}
                    >
                      Intro
                    </Heading>
                  }

                  {featuredParagraph &&
                    <div
                      id="intro"
                      className="copy--feature"
                      style={styles.featured}
                      dangerouslySetInnerHTML={{ __html: `${featuredParagraph}` }}
                    />
                  }

                  {featuredContent &&
                    <div
                      className="NarrativeBodyText"
                      dangerouslySetInnerHTML={{ __html: `${featuredContent}` }}
                    />
                  }
                </div>
              }

              {poi.showWorldNomads &&
                <div ref={node => { this.worldNomadsWidget = node; }}>
                  <WorldNomadsWidget />
                </div>
              }

              {poi.showFlightWidget &&
                <div ref={node => { this.flightQuoteWidget = node; }}>
                  <FlightQuote place={place} />
                </div>
              }

            </div>

            {poi.sections &&
              <div className="sections">
                {poi.sections.map((section, i) => (
                  <NarrativeContentSection
                    key={`${section.title}-${i}`}
                    setActiveLink={this.setActiveLink}
                    id={generateTitleId(section.title)}
                    isFirstSection={i === 0}
                    index={i}
                    topLevelSections={topLevelSections}
                    {...section}
                  />
                ))}
              </div>
            }
          </div>
        </Container>
      </div>
    );
  }
}

NarrativeComponent.propTypes = {
  /**
   * The POI for this component
   */
  poi: PropTypes.object.isRequired,

  /**
   * The place data for the POI
   */
  place: PropTypes.object.isRequired,

  /**
   * React router params
   */
  params: PropTypes.object,

  /**
  * Whether or not the layout should be formatted for mobile screen sizes
  */
  isMobile: PropTypes.bool,

  /**
   * The breadcrumbs for this page
   */
  breadcrumb: PropTypes.arrayOf(Object),
};

const mapStateToProps = state => ({
  breadcrumb: state.breadcrumb,
  isMobile: state.mobile.isMobile,
});

export default radium(connect(mapStateToProps)(NarrativeComponent));


// WEBPACK FOOTER //
// ./assets/pois/components/narrative.jsx